import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import config from '../../config';
import {
  Page,
  SectionAsSeenOn,
  SectionCategoryCarousel,
  SectionHero,
  SectionHowItWorks,
  SectionItsProfit,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  ExternalLink,
} from '../../components';
import { TopbarContainer } from '../../containers';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';

import facebookImage from '../../assets/1200_x_630.png';
import twitterImage from '../../assets/600_x_314.png';
import css from './LandingPage.css';
import { getCurrentOrigin } from '../../util/geolocation';
import { locationBounds } from '../../util/googleMaps';

const { LatLng } = sdkTypes;

export const LandingPageComponent = props => {
  const { history, intl, location, scrollingDisabled } = props;
  const [searching, setSearching] = useState(false);

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org
  // We are using JSON-LD format
  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'LandingPage.schemaDescription' });
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;

  const onBrowseListing = e => {
    e.preventDefault();
    if (
      typeof window === 'undefined' ||
      (typeof window !== 'undefined' && !navigator.geolocation)
    ) {
      setSearching(false);
      history.push(
        `s?address=Sydney%20NSW%2C%20Australia&bounds=-33.578140996%2C151.343020992%2C-34.118344992%2C150.520928608&origin=-33.8688197%2C151.2092955`
      );
      return;
    }
    setSearching(true);
    getCurrentOrigin(
      pos => {
        if (pos && pos.coords && pos.coords.latitude & pos.coords.longitude) {
          const routes = routeConfiguration();
          const originLatLng = new LatLng(pos.coords.latitude, pos.coords.longitude);
          const originMaybe = { origin: originLatLng };
          const viewportBounds = locationBounds(originLatLng, 50000);
          const searchParams = {
            address: 'Current location',
            ...originMaybe,
            bounds: viewportBounds,
            mapSearch: true,
          };
          setSearching(false);
          history.push(createResourceLocatorString('SearchPage', routes, {}, searchParams));
        } else {
          setSearching(false);
          history.push(
            `s?address=Sydney%20NSW%2C%20Australia&bounds=-33.578140996%2C151.343020992%2C-34.118344992%2C150.520928608&origin=-33.8688197%2C151.2092955`
          );
        }
      },
      error => {
        setSearching(false);
        history.push(
          `s?address=Sydney%20NSW%2C%20Australia&bounds=-33.578140996%2C151.343020992%2C-34.118344992%2C150.520928608&origin=-33.8688197%2C151.2092955`
        );
      }
    );
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        { url: `${config.canonicalRootURL}${twitterImage}`, width: 600, height: 314 },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.heroContainer}>
            <SectionHero
              className={css.hero}
              history={history}
              location={location}
              onBrowseListing={onBrowseListing}
              searching={searching}
            />
          </div>
          <ul className={css.sections}>
            <li className={css.section}>
              <div className={css.sectionContentFirstChild}>
                <SectionCategoryCarousel />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionHowItWorks />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionItsProfit />
              </div>
            </li>
            <li className={css.section}>
              <div className={css.sectionContent}>
                <SectionAsSeenOn />
              </div>
            </li>
          </ul>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool, object } = PropTypes;

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,

  // from withRouter
  history: object.isRequired,
  location: object.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(withRouter, connect(mapStateToProps), injectIntl)(LandingPageComponent);

export default LandingPage;
